import { memo, useEffect, useState } from 'react'
import type { NodeProps } from '@xyflow/react'
import { Handle, Position } from '@xyflow/react'
import { estimateUpscale, type Dimensions, type UpscaleOptions } from '../../shared/services/upscaleService'
import { UpscaleControls, usableSize } from '../../shared/components/UpscaleControls'
import { SkillGlyph } from '@shared/components/SkillGlyph'

/**
 * The Upscale card
 * -------------------------------------------------------------
 * Pictures wired into its one port on the left, as many as a run takes; the
 * bigger copies come out on the right, one frame each, filling as Topaz
 * finishes them (routes/upscale.ts). The settings are the same controls the
 * page and the Miro panel use, folded away like the picture card's until
 * somebody wants them.
 */

export interface UpscaleNodeData extends Record<string, unknown> {
  skillRef: string
  name: string
  glyph: string
  glow: string
  open?: boolean
  model?: string
  size?: string
  face?: boolean
  /** Runs in flight from this card, by id. */
  runs?: string[]
  error?: string
  /** When a press last ended without starting a run: clears "Starting…". */
  tried?: number
  /** What the last run actually cost. */
  credits?: number
  madeCount?: number

  /* handed in by the board, never stored */
  options?: UpscaleOptions | null
  live?: number
  maxLive?: number
  /** The wired pictures' sizes, where known. Their count is how many are wired. */
  inputs?: Dimensions[]
  onEdit?: (patch: Partial<UpscaleNodeData>) => void
  onRun?: () => void
}

export const UpscaleNode = memo(function UpscaleNode({ data, selected }: NodeProps) {
  const card = data as UpscaleNodeData
  // Open unless somebody folded it (Huanrui, 2026-10-06). A card saved before
  // this, with no answer either way, opens too.
  const isOpen = card.open !== false
  const options = card.options ?? null
  const inputs = card.inputs ?? []
  const wired = inputs.length
  const mine = (card.runs ?? []).length
  const live = card.live ?? mine
  const full = live >= (card.maxLive ?? 5)
  const edit = (patch: Partial<UpscaleNodeData>) => card.onEdit?.(patch)

  /*
   * Pressed, and not yet answered. The press is said at once, so a click is
   * never met with nothing while the run is being written down (2026-10-05:
   * "clicking does nothing").
   */
  const [starting, setStarting] = useState(false)
  useEffect(() => {
    setStarting(false)
  }, [mine, card.error, card.tried])
  useEffect(() => {
    if (!starting) return
    const stop = setTimeout(() => setStarting(false), 15000)
    return () => clearTimeout(stop)
  }, [starting])

  const model =
    options?.models.find((one) => one.key === card.model) ??
    options?.models.find((one) => one.key === options.defaultModel) ??
    options?.models[0]
  const sizeValue = options ? usableSize(options, card.size ?? '2k', inputs, model?.key) : card.size ?? '2k'
  const size = options?.sizes.find((one) => one.value === sizeValue)
  const estimate = options && model && size ? estimateUpscale(options, model, size, inputs) : 0
  const tooMany = options ? wired > options.maxPictures : false

  return (
    <div
      className={[
        'cnode',
        'pnode',
        'unode',
        isOpen ? 'pnode--open' : '',
        selected ? 'cnode--selected' : '',
        wired > 0 ? 'cnode--lit' : '',
        mine > 0 ? 'cnode--running' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ '--tile-glow': card.glow } as React.CSSProperties}
    >
      <Handle type="target" id="in" position={Position.Left} className="cnode__port" />
      <Handle type="source" id="out" position={Position.Right} className="cnode__port" />

      <div className="pnode__head">
        <span className="cnode__glyph" aria-hidden="true">
          <SkillGlyph glyph={card.glyph} />
        </span>
        <span className="cnode__name">{card.name}</span>
        <button
          type="button"
          className="pnode__toggle nodrag"
          onClick={() => edit({ open: !isOpen })}
          title={isOpen ? 'Fold this card' : 'Unfold this card'}
          aria-label={isOpen ? 'Fold this card' : 'Unfold this card'}
        >
          {isOpen ? '−' : '+'}
        </button>
      </div>

      {isOpen ? (
        <div className="pnode__body">
          {options ? (
            <UpscaleControls
              compact
              options={options}
              value={{ model: model?.key ?? '', size: sizeValue, face: card.face === true }}
              pictures={inputs}
              onChange={(patch) => edit(patch)}
            />
          ) : (
            <p className="pnode__error">Upscale could not be loaded.</p>
          )}
          {options && !options.ready ? <p className="pnode__error">Upscale is not switched on yet.</p> : null}
          {tooMany && options ? <p className="pnode__error">{options.maxPictures} pictures at a time is the most.</p> : null}
          {card.error ? <p className="pnode__error">{card.error}</p> : null}
        </div>
      ) : null}

      {isOpen || mine > 0 ? (
        <button
          type="button"
          className="cnode__run pnode__run nodrag"
          onClick={() => {
            setStarting(true)
            card.onRun?.()
          }}
          disabled={starting || full || wired === 0 || tooMany || !model || !options?.ready}
          title={
            full
              ? `${live} already going, here or elsewhere — wait for one to finish`
              : wired === 0
                ? 'Connect a picture first'
                : 'Make bigger copies of the pictures wired in'
          }
        >
          {starting ? (
            'Starting…'
          ) : full ? (
            'Queued — ' + live + ' already going'
          ) : (
            <>
              Run
              {estimate > 0 ? <span className="pnode__credits">about {estimate} credits</span> : null}
              {mine > 0 ? <span className="pnode__credits">· {mine} going</span> : null}
            </>
          )}
        </button>
      ) : null}

      {mine === 0 && card.credits ? (
        <span className="pnode__spent">
          {card.madeCount ?? 0} made · {card.credits} credits
        </span>
      ) : null}

      {!isOpen && card.error ? <span className="cnode__error">{card.error}</span> : null}
    </div>
  )
})
